import axios from 'axios'
import { useCallback, useRef, useState } from 'react'
import { AssinaturaType, PrescricaoSituation, getApiErrorMessage } from '_metronic/helpers'
import { SNCR_CODIGO } from '../../../sncr/core/_models'
import { codigoErroSncr } from '../../../sncr/core/erros'
import { ItemPrescricao } from '../core/montagem/itens'
import { montarRequisicoes } from '../core/montagem/requisicoes'
import { PrescricaoView } from '../core/_model'
import {
  completePrescricao,
  createExame,
  createFormula,
  createMedicamento,
  createOrientacao,
  deletePrescricao,
  getPrescricaoById,
} from '../core/_requests'

export type EstadoDocumento = 'fila' | 'assinando' | 'assinado' | 'erro'

export type DocumentoEmAssinatura = {
  prescricao: PrescricaoView
  estado: EstadoDocumento
  erro?: string
  /** Falhou por falta de número SNCR: a saída é retirar um bloco e tentar de novo. */
  semSaldoSncr?: boolean
}

/**
 * Cria os rascunhos (uma requisição por categoria; o backend divide em documentos) e assina um
 * por vez, para a tela mostrar o progresso de cada um. Um documento com erro não interrompe os
 * outros e pode ser tentado de novo sozinho. Nada é gravado antes de o certificado estar pronto.
 */
export const useAssinaturaDocumentos = (atendimentoId: number) => {
  const [documentos, setDocumentos] = useState<DocumentoEmAssinatura[]>([])
  const [preparando, setPreparando] = useState(false)
  const [erroPreparo, setErroPreparo] = useState<string>()
  /** Rascunhos que deviam ter sido apagados e não foram: ficam no backend sem dono na tela. */
  const [rascunhosNaoApagados, setRascunhosNaoApagados] = useState(0)

  // O laço de assinatura é assíncrono: lê a lista e o certificado mais recentes por ref.
  const documentosRef = useRef(documentos)
  documentosRef.current = documentos
  const tipoRef = useRef<AssinaturaType>()
  // Uma execução por vez: dois cliques em "Tentar de novo" criariam rascunhos em dobro.
  const emVooRef = useRef(false)

  /** Apaga rascunhos e conta os que falharam, para a tela avisar em vez de esconder. */
  const apagar = useCallback(async (ids: number[]) => {
    const resultados = await Promise.allSettled(ids.map((id) => deletePrescricao(id)))
    const falhas = resultados.filter((r) => r.status === 'rejected').length
    if (falhas > 0) setRascunhosNaoApagados((atual) => atual + falhas)
    return falhas
  }, [])

  const atualizar = useCallback(
    (id: number, campos: Partial<DocumentoEmAssinatura>) =>
      setDocumentos((atuais) =>
        atuais.map((doc) => (doc.prescricao.id === id ? { ...doc, ...campos } : doc))
      ),
    []
  )

  const criarRascunhos = useCallback(
    async (itens: ItemPrescricao[]) => {
      const requisicoes = montarRequisicoes(itens)
      const criados: PrescricaoView[] = []
      try {
        if (requisicoes.medicamentos) {
          criados.push(...(await createMedicamento(atendimentoId, requisicoes.medicamentos)))
        }
        if (requisicoes.formulas) {
          criados.push(...(await createFormula(atendimentoId, requisicoes.formulas)))
        }
        if (requisicoes.exames) {
          criados.push(...(await createExame(atendimentoId, requisicoes.exames)))
        }
        if (requisicoes.orientacoes) {
          criados.push(...(await createOrientacao(atendimentoId, requisicoes.orientacoes)))
        }
        return criados
      } catch (error) {
        // Sem meio-termo: se uma categoria falhou, os rascunhos já criados não ficam órfãos.
        await apagar(criados.map((prescricao) => prescricao.id))
        throw error
      }
    },
    [atendimentoId, apagar]
  )

  const assinarUm = useCallback(
    async (prescricao: PrescricaoView, tipo: AssinaturaType) => {
      atualizar(prescricao.id, { estado: 'assinando', erro: undefined, semSaldoSncr: false })
      try {
        const assinada = await completePrescricao({
          prescricao: prescricao.id,
          dadosPaciente: null,
          tipo,
        })
        atualizar(prescricao.id, { estado: 'assinado', prescricao: assinada })
      } catch (error) {
        // Reenvio depois de uma interrupção: se o documento já saiu de rascunho, ele está assinado.
        if (axios.isAxiosError(error) && error.response?.status === 422) {
          const atual = await getPrescricaoById(prescricao.id).catch(() => undefined)
          if (atual?.situacao === PrescricaoSituation.Assinada) {
            atualizar(prescricao.id, { estado: 'assinado', prescricao: atual })
            return
          }
        }
        atualizar(prescricao.id, {
          estado: 'erro',
          erro: getApiErrorMessage(error) ?? (error as Error)?.message,
          semSaldoSncr: codigoErroSncr(error) === SNCR_CODIGO.SALDO_INSUFICIENTE,
        })
      }
    },
    [atualizar]
  )

  /** Cria os rascunhos, se ainda não existem, e assina todos os que não estão assinados. */
  const assinar = useCallback(
    async (itens: ItemPrescricao[], tipo: AssinaturaType) => {
      if (emVooRef.current) return
      emVooRef.current = true
      tipoRef.current = tipo
      try {
        let lista = documentosRef.current
        if (lista.length === 0) {
          setPreparando(true)
          setErroPreparo(undefined)
          try {
            const criados = await criarRascunhos(itens)
            lista = criados.map((prescricao) => ({ prescricao, estado: 'fila' as const }))
            setDocumentos(lista)
          } catch (error) {
            setErroPreparo(getApiErrorMessage(error) ?? (error as Error)?.message)
            return
          } finally {
            setPreparando(false)
          }
        }
        for (const doc of lista.filter((d) => d.estado !== 'assinado')) {
          await assinarUm(doc.prescricao, tipo)
        }
      } finally {
        emVooRef.current = false
      }
    },
    [assinarUm, criarRascunhos]
  )

  /** Tenta de novo os documentos com erro (todos, ou só os `ids` informados). */
  const tentarDeNovo = useCallback(
    async (ids?: number[]) => {
      const tipo = tipoRef.current
      if (!tipo || emVooRef.current) return
      emVooRef.current = true
      try {
        const comErro = documentosRef.current.filter(
          (doc) => doc.estado === 'erro' && (!ids || ids.includes(doc.prescricao.id))
        )
        for (const doc of comErro) await assinarUm(doc.prescricao, tipo)
      } finally {
        emVooRef.current = false
      }
    },
    [assinarUm]
  )

  /**
   * Apaga os rascunhos que não foram assinados e tira eles da lista. Os assinados ficam: existem
   * no backend e continuam aparecendo no resultado. Devolve quantos não puderam ser apagados.
   */
  const descartarPendentes = useCallback(async () => {
    const pendentes = documentosRef.current.filter((doc) => doc.estado !== 'assinado')
    const falhas = await apagar(pendentes.map((doc) => doc.prescricao.id))
    setDocumentos((atuais) => atuais.filter((doc) => doc.estado === 'assinado'))
    return falhas
  }, [apagar])

  /** Esquece a sessão (prescrição nova). Chame `descartarPendentes` antes. */
  const reiniciar = useCallback(() => {
    setDocumentos([])
    setErroPreparo(undefined)
    setRascunhosNaoApagados(0)
  }, [])

  /** Troca a prescrição de um documento já assinado (ex.: depois de gerar o LME). */
  const recarregar = useCallback(async (ids: number[]) => {
    const atualizadas = await Promise.all(ids.map((id) => getPrescricaoById(id)))
    setDocumentos((atuais) =>
      atuais.map((doc) => {
        const nova = atualizadas.find((p) => p.id === doc.prescricao.id)
        return nova ? { ...doc, prescricao: nova } : doc
      })
    )
  }, [])

  const emAndamento = preparando || documentos.some((doc) => doc.estado === 'assinando')
  const pendentes = documentos.some((doc) => doc.estado === 'fila')

  return {
    documentos,
    preparando,
    erroPreparo,
    rascunhosNaoApagados,
    concluido: documentos.length > 0 && !emAndamento && !pendentes,
    emAndamento: emAndamento || pendentes,
    tipoCertificado: tipoRef.current,
    assinar,
    tentarDeNovo,
    descartarPendentes,
    reiniciar,
    recarregar,
  }
}
